import { API_URL, formatApiError } from '../utils/api';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { DataToolbar } from '../components/DataToolbar';
import { DataTable } from '../components/DataTable';
import {
  GraduationCap,
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Eye,
  Trash,
  MapPin,
  Storefront,
  Phone,
  EnvelopeSimple,
  Bank,
  CurrencyDollar,
  Drop,
  ArrowsClockwise
} from '@phosphor-icons/react';
import { toast } from 'sonner';

// API_URL imported from utils

const initialFormData = {
  campus_name: '',
  short_name: '',
  entity_id: '',
  physical_addr: '',
  address_line1: '',
  address_line2: '',
  telephone: '',
  email: '',
  bank_acc_no: '',
  bank_acc_name: '',
  bank_name: '',
  subscription_fee: '0',
  initial_recharge: '0',
};

export default function CampusesPage() {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const { entities, selectedEntity } = useTenant();
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterEntity, setFilterEntity] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCampus, setEditingCampus] = useState(null);
  const [formData, setFormData] = useState({ ...initialFormData });
  const [step, setStep] = useState(0);

  const steps = [
    { label: 'Basic Info', icon: GraduationCap },
    { label: 'Address & Contact', icon: MapPin },
    { label: 'Banking', icon: Bank },
    { label: 'Pricing', icon: CurrencyDollar },
  ];

  const fetchCampuses = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let url = `${API_URL}/api/campuses`;
      if (filterEntity && filterEntity !== 'all') {
        url += `?entity_id=${filterEntity}`;
      }
      const { data } = await axios.get(url, { withCredentials: true, headers });
      setCampuses(data);
    } catch (error) {
      console.error('Error fetching campuses:', error);
      toast.error('Failed to fetch campuses');
    } finally {
      setLoading(false);
    }
  }, [filterEntity]);

  const syncFromMiddleware = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const entityParam = filterEntity && filterEntity !== 'all' ? `?entity_id=${filterEntity}` : '';
      await axios.get(`${API_URL}/api/campuses/sync-middleware${entityParam}`, { withCredentials: true, headers });
      await fetchCampuses();
    } catch (e) { /* silent */ }
  }, [filterEntity, fetchCampuses]);

  useEffect(() => {
    fetchCampuses();
    syncFromMiddleware();
  }, [fetchCampuses, syncFromMiddleware]);

  const resetForm = () => {
    setFormData({ ...initialFormData, entity_id: selectedEntity?.id || '' });
    setEditingCampus(null);
    setStep(0);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditDialog = (campus) => {
    setEditingCampus(campus);
    setStep(0);
    setFormData({
      campus_name: campus.campus_name || '',
      short_name: campus.short_name || campus.campus_code || '',
      entity_id: campus.entity_id || '',
      physical_addr: campus.physical_addr || '',
      address_line1: campus.address_line1 || '',
      address_line2: campus.address_line2 || '',
      telephone: campus.telephone || '',
      email: campus.email || '',
      bank_acc_no: campus.bank_acc_no || '',
      bank_acc_name: campus.bank_acc_name || '',
      bank_name: campus.bank_name || '',
      subscription_fee: String(campus.subscription_fee || 0),
      initial_recharge: String(campus.initial_recharge || 0),
    });
    setIsDialogOpen(true);
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const payload = {
        ...formData,
        subscription_fee: parseFloat(formData.subscription_fee) || 0,
        initial_recharge: parseFloat(formData.initial_recharge) || 0,
      };

      if (editingCampus) {
        await axios.put(`${API_URL}/api/campuses/${editingCampus.id}`, payload, {
          withCredentials: true, headers
        });
        toast.success('Campus updated successfully');
      } else {
        await axios.post(`${API_URL}/api/campuses`, payload, {
          withCredentials: true, headers
        });
        toast.success('Campus created successfully');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchCampuses();
    } catch (error) {
      const message = formatApiError(error, 'Failed to save campus');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (campus) => {
    if (!window.confirm(`Are you sure you want to delete ${campus.campus_name}?`)) return;
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API_URL}/api/campuses/${campus.id}`, {
        withCredentials: true, headers
      });
      toast.success('Campus deleted successfully');
      fetchCampuses();
    } catch (error) {
      toast.error('Failed to delete campus');
    }
  };

  const filteredCampuses = campuses.filter(campus =>
    campus.campus_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (campus.short_name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-3 pb-6" data-testid="campuses-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-[#1C1917] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Campuses
        </h1>
        <div className="flex gap-2">
          <Button
            onClick={openCreateDialog}
            className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
            data-testid="create-campus-btn"
          >
            <Plus size={18} className="mr-2" />
            Add Campus
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <DataToolbar
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search by name or short code…"
        testId="campuses-toolbar"
        onClearAll={() => { setSearchQuery(''); setFilterEntity('all'); }}
        filters={isSuperAdmin ? [{
          key: 'entity',
          label: 'Entity',
          value: filterEntity,
          onChange: setFilterEntity,
          options: [{ value: 'all', label: 'All entities' }, ...entities.map((e) => ({ value: e.id, label: e.entity_name }))],
        }] : []}
      />

      <DataTable
        columns={[
          { key: 'campus', label: 'Campus', width: 'minmax(220px,2fr)', render: (c) => (
            <span className="flex items-center gap-3 min-w-0">
              <span className="shrink-0 w-8 h-8 rounded-lg bg-[#0A2540]/10 group-hover:bg-[#EDE6D8] flex items-center justify-center">
                <GraduationCap size={16} className="text-[#0A2540] group-hover:text-[#1C1917]" />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-[#1C1917] group-hover:text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {c.campus_name}
                </span>
              </span>
            </span>
          ) },
          ...(isSuperAdmin ? [{ key: 'entity_name', label: 'Entity', width: 'minmax(140px,1.2fr)', tone: 'muted' }] : []),
          { key: 'telephone', label: 'Contact', width: 'minmax(140px,1.2fr)', mono: true, tone: 'muted', render: (c) => c.telephone || c.email || '—' },
          { key: 'subscription_fee', label: 'Sub. Fee', width: 'minmax(100px,0.9fr)', mono: true, align: 'right', tone: 'strong',
            render: (c) => Number(c.subscription_fee || 0).toLocaleString() },
          { key: 'initial_recharge', label: 'Init. Recharge', width: 'minmax(110px,1fr)', mono: true, align: 'right', tone: 'strong',
            render: (c) => Number(c.initial_recharge || 0).toLocaleString() },
          { key: 'stores_count', label: 'Stores', width: 'minmax(80px,0.6fr)', mono: true, align: 'right', tone: 'strong',
            render: (c) => Number(c.stores_count || 0).toLocaleString() },
          { key: 'status', label: 'Status', width: 'minmax(100px,0.9fr)', render: (c) => {
            const isActive = String(c.status || '').toLowerCase() === 'active';
            return (
              <div className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-rose-500'} group-hover:bg-[#FFFFFF]`} />
                <span className={`text-xs font-medium ${isActive ? 'text-emerald-700' : 'text-rose-700'} group-hover:text-[#1C1917]`} style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {c.status || '—'}
                </span>
              </div>
            );
          } },
        ]}
        rows={filteredCampuses}
        loading={loading}
        rowKey={(c) => c.id}
        onRowClick={(c) => navigate(`/campuses/${c.id}`)}
        emptyIcon={<GraduationCap size={20} className="text-slate-500" />}
        emptyTitle="No campuses yet"
        emptyText={searchQuery ? 'Try clearing your search.' : 'Add a campus or sync from middleware.'}
        testIdPrefix="campus"
        actionRender={(c) => (
          <span className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); navigate(`/campuses/${c.id}`); }}
              className="text-[#0A2540] hover:text-[#0A2540] hover:bg-[#0A2540]/10 group-hover:text-[#1C1917] group-hover:bg-[#EDE6D8] h-8 w-8 p-0"
              data-testid={`view-campus-${c.id}`}
              aria-label="View campus"
            >
              <Eye size={14} />
            </Button>
          </span>
        )}
      />

      {/* Create/Edit Dialog — Multi-Step */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) { setStep(0); } setIsDialogOpen(open); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {editingCampus ? 'Edit Campus' : 'Create Campus'}
            </DialogTitle>
          </DialogHeader>

          {/* Stepper */}
          <div className="flex items-center justify-between mb-6 px-2">
            {steps.map((s, i) => {
              const Icon = s.icon;
              const isActive = i === step;
              const isDone = i < step;
              return (
                <div key={s.label} className="flex items-center flex-1 last:flex-none">
                  <button
                    type="button"
                    onClick={() => setStep(i)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      isActive
                        ? 'bg-[#0A2540] text-white'
                        : isDone
                          ? 'bg-[#10B981]/10 text-[#10B981]'
                          : 'bg-[#FCFAF6] text-[#78716C]'
                    }`}
                    data-testid={`step-${i}`}
                  >
                    <Icon size={14} />
                    <span className="hidden sm:inline">{s.label}</span>
                    <span className="sm:hidden">{i + 1}</span>
                  </button>
                  {i < steps.length - 1 && (
                    <div className={`flex-1 h-px mx-2 ${isDone ? 'bg-[#10B981]/30' : 'bg-[#EDE6D8]'}`} />
                  )}
                </div>
              );
            })}
          </div>

          <form onSubmit={handleSubmit}>
            {/* Step 0: Basic Information */}
            {step === 0 && (
              <div className="space-y-4" data-testid="step-basic-info">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Campus Name <span className="text-[#EF4444]">*</span></Label>
                    <Input
                      value={formData.campus_name}
                      onChange={(e) => updateField('campus_name', e.target.value)}
                      placeholder="e.g., University of Dodoma"
                      className="border-[#EDE6D8] rounded-lg"
                      data-testid="campus-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Short Name <span className="text-[#EF4444]">*</span></Label>
                    <Input
                      value={formData.short_name}
                      onChange={(e) => updateField('short_name', e.target.value)}
                      placeholder="e.g., UDOM"
                      className="border-[#EDE6D8] rounded-lg"
                      data-testid="campus-short-name-input"
                    />
                  </div>
                </div>
                {isSuperAdmin && (
                  <div className="space-y-2">
                    <Label>Entity <span className="text-[#EF4444]">*</span></Label>
                    <Select
                      value={formData.entity_id}
                      onValueChange={(value) => updateField('entity_id', value)}
                    >
                      <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="campus-entity-select">
                        <SelectValue placeholder="Select entity" />
                      </SelectTrigger>
                      <SelectContent>
                        {entities.map((entity) => (
                          <SelectItem key={entity.id} value={entity.id}>
                            {entity.entity_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {/* Step 1: Address & Contact */}
            {step === 1 && (
              <div className="space-y-4" data-testid="step-address-contact">
                <p className="text-xs text-[#78716C] mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Provide the campus location and contact details.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Physical Address</Label>
                    <Input
                      value={formData.physical_addr}
                      onChange={(e) => updateField('physical_addr', e.target.value)}
                      placeholder="e.g., 123 Main Street, City"
                      className="border-[#EDE6D8] rounded-lg"
                      data-testid="campus-physical-addr-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Address Line 1</Label>
                    <Input
                      value={formData.address_line1}
                      onChange={(e) => updateField('address_line1', e.target.value)}
                      placeholder="e.g., P.O. Box 123"
                      className="border-[#EDE6D8] rounded-lg"
                      data-testid="campus-address-line1-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Address Line 2</Label>
                    <Input
                      value={formData.address_line2}
                      onChange={(e) => updateField('address_line2', e.target.value)}
                      placeholder="e.g., City Center"
                      className="border-[#EDE6D8] rounded-lg"
                      data-testid="campus-address-line2-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Telephone</Label>
                    <Input
                      value={formData.telephone}
                      onChange={(e) => updateField('telephone', e.target.value)}
                      placeholder="e.g., 255700000000"
                      className="border-[#EDE6D8] rounded-lg"
                      data-testid="campus-telephone-input"
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      placeholder="e.g., info@example.com"
                      className="border-[#EDE6D8] rounded-lg"
                      data-testid="campus-email-input"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Banking Details */}
            {step === 2 && (
              <div className="space-y-4" data-testid="step-banking">
                <p className="text-xs text-[#78716C] mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Add the campus banking details for payment collection.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Bank Name</Label>
                    <Input
                      value={formData.bank_name}
                      onChange={(e) => updateField('bank_name', e.target.value)}
                      placeholder="e.g., CRDB Bank"
                      className="border-[#EDE6D8] rounded-lg"
                      data-testid="campus-bank-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Account Name</Label>
                    <Input
                      value={formData.bank_acc_name}
                      onChange={(e) => updateField('bank_acc_name', e.target.value)}
                      placeholder="Account holder name"
                      className="border-[#EDE6D8] rounded-lg"
                      data-testid="campus-bank-acc-name-input"
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Account Number</Label>
                    <Input
                      value={formData.bank_acc_no}
                      onChange={(e) => updateField('bank_acc_no', e.target.value)}
                      placeholder="Account number"
                      className="border-[#EDE6D8] rounded-lg"
                      data-testid="campus-bank-acc-no-input"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Pricing */}
            {step === 3 && (
              <div className="space-y-4" data-testid="step-pricing">
                <p className="text-xs text-[#78716C] mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Set the subscription fee and the initial recharge amount applied when a subscriber is onboarded.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <CurrencyDollar size={12} className="text-[#0A2540]" />
                      Subscription Fee
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.subscription_fee}
                      onChange={(e) => updateField('subscription_fee', e.target.value)}
                      placeholder="e.g., 5000"
                      className="border-[#EDE6D8] rounded-lg"
                      data-testid="campus-subscription-fee-input"
                    />
                    <p className="text-[11px] text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      One-time fee charged on subscriber onboarding.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <Drop size={12} className="text-[#0A2540]" />
                      Initial Recharge
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.initial_recharge}
                      onChange={(e) => updateField('initial_recharge', e.target.value)}
                      placeholder="e.g., 2000"
                      className="border-[#EDE6D8] rounded-lg"
                      data-testid="campus-initial-recharge-input"
                    />
                    <p className="text-[11px] text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      Starting card balance loaded at activation.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Footer */}
            <DialogFooter className="pt-6 mt-6 border-t border-[#EDE6D8] flex justify-between">
              <div>
                {step > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(step - 1)}
                    className="border-[#EDE6D8] rounded-lg"
                    data-testid="step-back-btn"
                  >
                    Back
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="border-[#EDE6D8] rounded-lg"
                >
                  Cancel
                </Button>
                {step < steps.length - 1 ? (
                  <Button
                    type="button"
                    onClick={() => {
                      if (step === 0 && (!formData.campus_name || !formData.short_name)) {
                        toast.error('Campus Name and Short Name are required');
                        return;
                      }
                      setStep(step + 1);
                    }}
                    className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
                    data-testid="step-next-btn"
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
                    data-testid="save-campus-btn"
                  >
                    {submitting ? 'Saving...' : (editingCampus ? 'Update' : 'Create')}
                  </Button>
                )}
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
